import { type Listing, VARIANTS, type Variant } from "@app/protocol";
import { useEffect, useState } from "react";
import { API_BASE } from "../config";
import { useT } from "../i18n/context";
import { targetLabel } from "../routes/lobbyText";
import { useStore } from "../store";
import { openTable } from "../ws/singleton";
import { Avatar } from "./Avatar";
import { Choice } from "./Choices";

const POLL_MS = 4_000;

/** The open tables from the server; polled while the home screen is up and the tab is visible (0012 S11). */
export function useOpenTables(): Listing[] {
  const session = useStore((s) => s.session);
  const [tables, setTables] = useState<Listing[]>([]);
  useEffect(() => {
    if (session === null) return;
    let alive = true;
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE}/tables`, {
          headers: { Authorization: `Bearer ${session}` },
        });
        const body = (await response.json()) as { tables?: Listing[] };
        if (alive && Array.isArray(body.tables)) setTables(body.tables);
      } catch {
        // The list is a convenience; the next poll tries again.
      }
    };
    void load();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [session]);
  return tables;
}

function Row({ table, yours }: { table: Listing; yours: boolean }) {
  const { t } = useT();
  const name = table.host?.name ?? "?";
  // A match in progress has no seat to take: it is there to watch (review 0024 decision 176).
  const playing = table.status === "playing";
  const state = playing ? t("home.playing") : t("home.seatsOpen", { open: table.open });
  const meta = [
    t(`variant.${table.variant}`),
    targetLabel(t, { ...table, rules: {} }),
    t(`tempo.${table.tempo}`),
  ].join(" · ");
  return (
    <li
      data-table={table.id}
      className="flex items-center gap-3.5 border-t border-ink/8 py-3 first:border-t-0"
    >
      <Avatar member={table.host} size={40} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[15px] font-bold">{name}</span>
        <span className="truncate text-[12px] text-ink/70">{meta}</span>
        <span
          className={`text-[12px] font-bold @[480px]:hidden ${playing ? "text-ink/70" : "text-success"}`}
        >
          {state}
        </span>
      </span>
      <span
        className={`hidden whitespace-nowrap text-right text-[12px] font-bold @[480px]:inline ${playing ? "text-ink/70" : "text-success"}`}
      >
        {state}
      </span>
      <button
        type="button"
        className={`flex-none rounded-pill px-[18px] py-2 text-[13px] font-extrabold disabled:opacity-40 ${playing ? "btn-glass" : "btn-green"}`}
        aria-label={t(playing ? "home.watchTable" : "home.joinTable", { name })}
        disabled={yours && !playing}
        onClick={() => openTable(table.id)}
      >
        {t(playing ? "home.watch" : "home.join")}
      </button>
    </li>
  );
}

/** Join a table (design 2b): filter by game, the list, or a note that leads to setting one up (0012 U11). */
export function OpenTables({
  tables,
  onCreate,
}: {
  tables: readonly Listing[];
  onCreate: () => void;
}) {
  const { t } = useT();
  const you = useStore((s) => s.member?.userId ?? null);
  const [filter, setFilter] = useState<Variant | null>(null);
  const shown = filter === null ? tables : tables.filter((table) => table.variant === filter);
  return (
    <section aria-labelledby="tables-title" className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="tables-title" className="text-legible text-[26px] font-extrabold">
          {t("home.tables")}
        </h2>
        <div className="flex flex-wrap gap-2">
          <Choice
            label={t("home.filter.all")}
            selected={filter === null}
            onClick={() => setFilter(null)}
          />
          {VARIANTS.map((variant) => (
            <Choice
              key={variant}
              label={t(`variant.${variant}`)}
              selected={filter === variant}
              onClick={() => setFilter(variant)}
            />
          ))}
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="flex flex-col items-start gap-3 rounded-[16px] border-[1.5px] border-edge bg-panel-strong p-4 shadow-pill">
          <p className="text-[14px] text-ink/80">{t("home.tables.empty")}</p>
          <button
            type="button"
            className="btn-gold h-11 rounded-pill px-5 text-[14px] font-extrabold"
            onClick={onCreate}
          >
            {t("home.tab.create")}
          </button>
        </div>
      ) : (
        <ul className="@container flex flex-col rounded-[16px] border-[1.5px] border-edge bg-panel-strong px-4 py-1.5 shadow-pill">
          {shown.map((table) => (
            <Row key={table.id} table={table} yours={table.host?.userId === you} />
          ))}
        </ul>
      )}
    </section>
  );
}
